import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEmpty, get, startCase } from 'lodash';
import { Empty, Row, Table, Button } from 'antd';
import generatePath from '~settings/routing';
import { dayFormatType, formatDay } from '~helpers/day';
import formatPrice from '~helpers/currency';
import { orderReceivableStatus } from '~constants/order';

export default function OrderTable({
  t,
  history,
  handleOpenUpdateModal,
  queryList,
  list,
  loading,
  setFilters,
  filters,
}) {
  const [pageSize, setPageSize] = useState(filters?.pageSize ?? 10);
  const [pageNumber, setPageNumber] = useState(filters?.pageNumber ?? 1);

  const viewButtonStatus = [
    orderReceivableStatus.READY_FOR_APPROVAL,
    orderReceivableStatus.PENDING_APPROVAL,
    orderReceivableStatus.PENDING_INVOICE,
    orderReceivableStatus.INVOICED,
    orderReceivableStatus.PARTIALLY_PAID,
  ];

  const getColumns = useMemo(
    () => [
      {
        title: t('cms.reconciliation.lead_generation.table.field.booking_number'),
        dataIndex: 'referenceId',
        key: 'referenceId',
        width: 160,
        render: (_, record) => (
          <span className="transfer__table-field">{record?.order?.referenceId ?? '-'}</span>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.landlord'),
        dataIndex: 'landlordName',
        key: 'landlordName',
        width: 176,
        render: (_, record) => (
          <span className="transfer__table-field">{record?.landlord?.name ?? '-'}</span>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.property'),
        dataIndex: 'propertyName',
        key: 'propertyName',
        width: 200,
        render: (_, record) => (
          <span className="transfer__table-field">{record?.order?.propertyName ?? '-'}</span>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.destination_country'),
        dataIndex: 'destinationCountry',
        key: 'destinationCountry',
        width: 200,
        render: (_, record) => (
          <span className="transfer__table-field">{record?.order?.city?.country?.name ?? '-'}</span>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.destination_city'),
        dataIndex: 'destinationCity',
        key: 'destinationCity',
        width: 200,
        render: (_, record) => (
          <div className="transfer__table-field">{record?.order?.city?.name ?? '-'}</div>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.students_name'),
        dataIndex: 'studentName',
        key: 'studentName',
        width: 176,
        render: (_, record) => (
          <div className="transfer__table-field">
            {`${record?.order?.student?.passportFirstName} ${
              record?.order?.student?.passportMiddleName ?? ''
            } ${record?.order?.student?.passportLastName}` ?? '-'}
          </div>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.total_rent'),
        dataIndex: 'totalRent',
        key: 'totalRent',
        width: 150,
        render: (_, record) => (
          <span className="transfer__table-field">
            {get(record, 'order.totalRental')
              ? formatPrice(get(record, 'order.totalRental'), get(record, 'order.currency'))
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.commission_trigger_point'),
        dataIndex: 'commissionTriggerPoint',
        key: 'commissionTriggerPoint',
        width: 250,
        render: (_, record) => (
          <span className="transfer__table-field">
            {startCase(record?.order?.commissionTriggerPoint?.toLowerCase()) || '-'}
          </span>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.commission_tier'),
        dataIndex: 'commissionTier',
        key: 'commissionTier',
        width: 144,
        render: (_, record) => (
          <span className="transfer__table-field">
            <Choose>
              <When condition={record?.commission?.commissionTierValue}>
                {record?.commission?.commissionTierValue &&
                  (record?.commission?.commissionTierType === 'PERCENTAGE'
                    ? record?.commission?.commissionTierValue + '%'
                    : record?.commission?.commissionTierValue)}
              </When>
              <Otherwise>-</Otherwise>
            </Choose>
          </span>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.total_commission'),
        dataIndex: 'totalCommission',
        key: 'totalCommission',
        width: 156,
        render: (_, record) => (
          <span className="transfer__table-field">
            {get(record, 'commission.totalPrice')
              ? formatPrice(
                  get(record, 'commission.totalPrice'),
                  get(record, 'commission.currency'),
                )
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.move_in_date'),
        dataIndex: 'moveInDate',
        key: 'moveInDate',
        width: 156,
        render: (_, record) => (
          <span className="transfer__table-field">
            {record?.order?.moveInDate
              ? formatDay(dayFormatType.UKFORMAT, record?.order?.moveInDate)
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.move_out_date'),
        dataIndex: 'moveOutDate',
        key: 'moveOutDate',
        width: 156,
        render: (_, record) => (
          <span className="transfer__table-field">
            {record?.order?.moveOutDate
              ? formatDay(dayFormatType.UKFORMAT, record?.order?.moveOutDate)
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.current_status'),
        dataIndex: 'status',
        key: 'status',
        width: 184,
        render: (_, record) => (
          <span className="transfer__table-field">
            {record?.status
              ? t(
                  `cms.reconciliation.lead_generation.table.filter.status.${record.status.toLowerCase()}`,
                )
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.invoice_number'),
        dataIndex: 'invoiceNumber',
        key: 'invoiceNumber',
        width: 150,
        render: (_, record) => (
          <span className="transfer__table-field">{record?.invoiceNumber ?? '-'}</span>
        ),
      },
      {
        title: t('cms.reconciliation.lead_generation.table.field.invoice_at'),
        dataIndex: 'invoicedAt',
        key: 'invoicedAt',
        width: 150,
        render: (_, record) => (
          <span className="transfer__table-field">
            {record?.invoicedAt ? formatDay(dayFormatType.UKFORMAT, record?.invoicedAt) : '-'}
          </span>
        ),
      },
      {
        title: t('cms.landlord.detail.listing_plan.label'),
        dataIndex: 'bookingJourney',
        key: 'bookingJourney',
        width: 150,
        render: (_, record) => (
          <span className="transfer__table-field">
            {record?.order?.bookingJourney
              ? t(
                  `cms.reconciliation.lead_generation.table.filter.status.${record?.order?.bookingJourney?.toLowerCase()}`,
                )
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.transfer.table.action.field'),
        key: 'action',
        fixed: 'right',
        width: 156,
        className: 'transfer__action',
        render: (_, record) => (
          <Row
            className={classNames('transfer__action-row', {
              'transfer__action-row--transferred': viewButtonStatus.includes(record?.status),
            })}
          >
            <button className="transfer__table-btn" onClick={() => handleViewBtn(record?.id)}>
              {t('cms.billing.transfer.table.action.view.btn')}
            </button>
            <If condition={viewButtonStatus.includes(record?.status)}>
              <button className="transfer__table-btn" onClick={() => handleOpenUpdateModal(record)}>
                {t('cms.change_log.entry_type.update')}
              </button>
            </If>
          </Row>
        ),
      },
    ],
    [],
  );

  const handleViewBtn = id => {
    history.push(generatePath('reconciliation.orderDetail', { id }));
  };

  const handlePaginationChange = number => {
    setPageNumber(number);
    setFilters({ ...filters, pageNumber: number });
    queryList({ variables: { ...filters, pageNumber: number } });
  };
  const handlePageSizeChange = (current, size) => {
    if (current !== size) {
      setPageSize(size);
      setPageNumber(1);
      setFilters({ ...filters, pageNumber: 1, pageSize: size });
      queryList({ variables: { ...filters, pageNumber: 1, pageSize: size } });
    }
  };

  useEffect(() => {
    if (isEmpty(filters)) {
      setPageSize(10);
      setPageNumber(1);
    }
  }, [filters]);

  return (
    <Choose>
      <When condition={list?.edges?.length === 0 && !loading}>
        <div
          className="reconciliation__table"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Empty
            description={
              <span className="try-again">
                {t('cms.reconciliation.lead_generation.table.filter.no_match')}
              </span>
            }
          >
            <Button
              type="ghost"
              icon="redo"
              onClick={() => {
                queryList();
                setFilters({});
              }}
            >
              {t('cms.reconciliation.lead_generation.table.filter.reset')}
            </Button>
          </Empty>
        </div>
      </When>
      <Otherwise>
        <Table
          className={classNames('reconciliation__table', {
            'reconciliation__auto-table': list?.edges?.length > 0,
          })}
          columns={getColumns}
          loading={loading}
          dataSource={list?.edges ?? []}
          pagination={{
            current: pageNumber,
            pageSize: pageSize,
            showSizeChanger: true,
            hideOnSinglePage: false,
            total: list?.totalCount,
            onChange: handlePaginationChange,
            onShowSizeChange: handlePageSizeChange,
          }}
        />
      </Otherwise>
    </Choose>
  );
}

OrderTable.propTypes = {
  t: PropTypes.func.isRequired,
  list: PropTypes.shape({
    edges: PropTypes.array,
    totalCount: PropTypes.number,
  }),
  history: PropTypes.func,
  queryList: PropTypes.func,
  handleOpenUpdateModal: PropTypes.func,
  loading: PropTypes.bool,
  filters: PropTypes.object,
  setFilters: PropTypes.func,
};

OrderTable.defaultProps = {
  t: () => {},
  list: {
    edges: [],
    totalCount: 0,
  },
  queryList: () => {},
  history: () => {},
  handleOpenUpdateModal: () => {},
  loading: false,
  filters: {},
  setFilters: () => {},
};
